import React from "react";
import {
  ArrowDown,
  ArrowRight,
  Archive,
  Check,
  Circle,
  Clock,
  Info,
  WarningCircle,
} from "@phosphor-icons/react";

const STATUS_PRESENTATION = {
  recorded: {
    Icon: Check,
    tone: "recorded",
  },
  missing: {
    Icon: Circle,
    tone: "missing",
  },
  review: {
    Icon: WarningCircle,
    tone: "review",
  },
  info: {
    Icon: Info,
    tone: "info",
  },
};

export default function EudamedReadinessPanel({
  projectName,
  roleLabel,
  roleView,
  items = [],
  summaryText,
  disclaimer,
  labels = {},
  onDownloadPackage,
  packageBusy = false,
  packageError,
  exportError,
  downloadBusyId = "",
  onJumpToDocuments,
  onJumpToGaps,
  hideGeneratedDocumentDownloads = false,
}) {
  const rows = Array.isArray(items) ? items : [];

  return (
    <section
      className="eudamed-panel overflow-hidden rounded-md border border-[var(--border)] bg-[var(--surface)]"
      data-testid="eudamed-readiness-panel"
      aria-labelledby="eudamed-panel-title"
    >
      <header className="relative border-b border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-5 sm:px-6 sm:py-6">
        <div className="eudamed-panel__header-rule" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="eudamed-panel__eyebrow">{labels.eyebrow}</span>
              {projectName ? (
                <span className="eudamed-panel__project">{projectName}</span>
              ) : null}
            </div>
            <h2
              id="eudamed-panel-title"
              className="text-xl font-extrabold tracking-tight text-[var(--text)] sm:text-2xl"
            >
              {labels.title}
            </h2>
            {summaryText ? (
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[var(--text-2)]">
                {summaryText}
              </p>
            ) : null}
          </div>

          <aside
            className="eudamed-panel__view shrink-0"
            aria-label={labels.roleViewAria}
            data-testid="eudamed-role-view"
          >
            <span className="eudamed-panel__view-label">
              {roleLabel}
            </span>
            {roleView ? (
              <span className="eudamed-panel__view-value">{roleView}</span>
            ) : null}
            <span className="eudamed-panel__view-note">
              {labels.roleViewNote}
            </span>
          </aside>
        </div>

        <div className="mt-5 flex flex-col gap-2 border-t border-[var(--border)] pt-4 text-xs leading-relaxed text-[var(--text-2)] sm:flex-row sm:items-center">
          <span className="eudamed-panel__signal" aria-hidden="true">
            <Info size={14} weight="bold" />
          </span>
          <p>
            {labels.statusScopeNote}
          </p>
        </div>
      </header>

      <div className="p-4 sm:p-6">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-bold text-[var(--text)]">{labels.recordsHeading}</h3>
            <p className="mt-1 text-xs text-[var(--text-2)]">
              {summaryText}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onJumpToDocuments}
              disabled={!onJumpToDocuments}
              className="eudamed-panel__secondary-action"
              data-testid="eudamed-jump-documents"
            >
              <ArrowRight size={14} weight="bold" />
              {labels.documentsAction}
            </button>
            <button
              type="button"
              onClick={onJumpToGaps}
              disabled={!onJumpToGaps}
              className="eudamed-panel__secondary-action"
              data-testid="eudamed-jump-gaps"
            >
              <ArrowRight size={14} weight="bold" />
              {labels.gapsAction}
            </button>
          </div>
        </div>

        {rows.length ? (
          <ul className="eudamed-panel__list" aria-label={labels.listAria}>
              {rows.map((item) => (
              <RecordRow
                key={item.id}
                item={item}
                labels={labels}
                downloadBusyId={downloadBusyId}
                hideGeneratedDocumentDownloads={hideGeneratedDocumentDownloads}
              />
            ))}
          </ul>
        ) : (
          <div className="eudamed-panel__empty" data-testid="eudamed-items-empty">
            <Archive size={20} weight="light" aria-hidden="true" />
            <span>{labels.emptyText}</span>
          </div>
        )}

        {disclaimer ? (
          <p
            className="mt-4 border-l-2 border-[var(--border-bright)] pl-3 text-xs leading-relaxed text-[var(--text-2)]"
            data-testid="eudamed-disclaimer"
          >
            {disclaimer}
          </p>
        ) : null}

        <div className="eudamed-panel__package mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-bold text-[var(--text)]">{labels.packageTitle}</div>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-2)]">
              {labels.packageDescription}
            </p>
          </div>
          <button
            type="button"
            onClick={onDownloadPackage}
            disabled={!onDownloadPackage || packageBusy}
            className="eudamed-panel__package-action"
            data-testid="eudamed-download-package"
          >
            {packageBusy ? (
              <Clock size={16} weight="bold" className="eudamed-panel__busy-icon" />
            ) : (
              <Archive size={16} weight="bold" />
            )}
            {packageBusy ? labels.packageBusy : labels.downloadPackage}
            {!packageBusy ? <ArrowDown size={14} weight="bold" /> : null}
          </button>
        </div>

        {packageError ? (
          <div
            className="eudamed-panel__error mt-3"
            role="alert"
            data-testid="eudamed-package-error"
          >
            <WarningCircle size={16} weight="bold" aria-hidden="true" />
            <span>{packageError}</span>
          </div>
        ) : null}
        {exportError ? (
          <div
            className="eudamed-panel__error mt-3"
            role="alert"
            data-testid="eudamed-export-error"
          >
            <WarningCircle size={16} weight="bold" aria-hidden="true" />
            <span>{exportError}</span>
          </div>
        ) : null}
      </div>

      <style>{`
        .eudamed-panel {
          color: var(--text);
          box-shadow: 0 12px 34px -26px rgba(0, 0, 0, 0.6);
        }
        .eudamed-panel__header-rule {
          position: absolute;
          inset: 0 auto 0 0;
          width: 3px;
          background: var(--accent);
          opacity: 0.88;
        }
        .eudamed-panel__eyebrow,
        .eudamed-panel__project,
        .eudamed-panel__view-label,
        .eudamed-panel__status {
          font-family: 'JetBrains Mono', monospace;
        }
        .eudamed-panel__eyebrow {
          color: var(--accent-text);
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.11em;
        }
        .eudamed-panel__project {
          max-width: 100%;
          overflow-wrap: anywhere;
          border-left: 1px solid var(--border-bright);
          padding-left: 9px;
          color: var(--text-muted);
          font-size: 10px;
          letter-spacing: 0.02em;
        }
        .eudamed-panel__view {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: 4px;
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--surface);
          padding: 10px 12px;
        }
        @media (min-width: 1024px) {
          .eudamed-panel__view { max-width: 240px; }
        }
        .eudamed-panel__view-label {
          color: var(--text-muted);
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }
        .eudamed-panel__view-value {
          color: var(--text);
          font-size: 13px;
          font-weight: 700;
        }
        .eudamed-panel__view-note {
          color: var(--text-2);
          font-size: 10px;
          line-height: 1.45;
        }
        .eudamed-panel__signal {
          display: inline-flex;
          flex: 0 0 auto;
          color: var(--accent-text);
        }
        .eudamed-panel__list {
          overflow: hidden;
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--surface);
        }
        .eudamed-panel__row {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 12px;
          padding: 14px;
          transition: background-color 140ms ease;
        }
        .eudamed-panel__row + .eudamed-panel__row {
          border-top: 1px solid var(--border);
        }
        .eudamed-panel__row:hover { background: var(--surface-elevated); }
        @media (min-width: 640px) {
          .eudamed-panel__row {
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
            gap: 18px;
            padding: 15px 16px;
          }
        }
        .eudamed-panel__row-copy { min-width: 0; }
        .eudamed-panel__row-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-start;
          gap: 7px;
        }
        @media (min-width: 640px) {
          .eudamed-panel__row-actions { justify-content: flex-end; }
        }
        .eudamed-panel__row-title {
          overflow-wrap: anywhere;
          color: var(--text);
          font-size: 13px;
          font-weight: 700;
          line-height: 1.45;
        }
        .eudamed-panel__row-detail {
          margin-top: 4px;
          overflow-wrap: anywhere;
          color: var(--text-2);
          font-size: 12px;
          line-height: 1.55;
        }
        .eudamed-panel__status {
          display: inline-flex;
          width: fit-content;
          align-items: center;
          gap: 6px;
          border: 1px solid var(--border);
          border-radius: 3px;
          padding: 5px 8px;
          font-size: 10px;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
        }
        .eudamed-panel__status--recorded {
          color: var(--risk-success);
          border-color: color-mix(in srgb, var(--risk-success) 34%, var(--border));
          background: color-mix(in srgb, var(--risk-success) 9%, var(--surface));
        }
        .eudamed-panel__status--missing {
          color: var(--text-2);
          background: var(--surface-elevated);
        }
        .eudamed-panel__status--review {
          color: var(--risk-warning);
          border-color: color-mix(in srgb, var(--risk-warning) 35%, var(--border));
          background: color-mix(in srgb, var(--risk-warning) 9%, var(--surface));
        }
        .eudamed-panel__status--info {
          color: var(--accent-text);
          border-color: color-mix(in srgb, var(--accent) 30%, var(--border));
          background: color-mix(in srgb, var(--accent) 8%, var(--surface));
        }
        .eudamed-panel__secondary-action,
        .eudamed-panel__package-action {
          display: inline-flex;
          min-height: 36px;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 1px solid var(--border-bright);
          border-radius: 4px;
          padding: 8px 11px;
          color: var(--text);
          font-size: 12px;
          font-weight: 600;
          transition: border-color 140ms ease, background-color 140ms ease, transform 140ms ease;
        }
        .eudamed-panel__secondary-action {
          background: var(--surface);
        }
        .eudamed-panel__secondary-action:hover:not(:disabled) {
          border-color: var(--accent);
          background: var(--surface-hover);
        }
        .eudamed-panel__package-action {
          flex: 0 0 auto;
          border-color: var(--accent);
          background: var(--accent);
          color: var(--bg);
          padding: 9px 13px;
        }
        .eudamed-panel__package-action:hover:not(:disabled) {
          transform: translateY(-1px);
          filter: brightness(1.06);
        }
        .eudamed-panel__secondary-action:focus-visible,
        .eudamed-panel__package-action:focus-visible,
        .eudamed-panel__download:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .eudamed-panel__secondary-action:disabled,
        .eudamed-panel__package-action:disabled,
        .eudamed-panel__download:disabled {
          cursor: not-allowed;
          opacity: 0.52;
        }
        .eudamed-panel__download {
          display: inline-flex;
          min-height: 34px;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: 1px solid var(--border);
          border-radius: 3px;
          background: var(--surface-elevated);
          padding: 7px 9px;
          color: var(--text-2);
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          transition: border-color 140ms ease, color 140ms ease, background-color 140ms ease;
        }
        .eudamed-panel__download:hover:not(:disabled) {
          border-color: var(--accent);
          background: var(--surface-hover);
          color: var(--text);
        }
        .eudamed-panel__empty {
          display: flex;
          min-height: 112px;
          align-items: center;
          justify-content: center;
          gap: 10px;
          border: 1px dashed var(--border-bright);
          border-radius: 4px;
          color: var(--text-2);
          font-size: 13px;
        }
        .eudamed-panel__package {
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--surface-elevated);
          padding: 14px;
        }
        .eudamed-panel__error {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          border: 1px solid color-mix(in srgb, var(--danger) 35%, var(--border));
          border-radius: 4px;
          background: color-mix(in srgb, var(--danger) 8%, var(--surface));
          padding: 10px 12px;
          color: var(--risk-danger);
          font-size: 12px;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }
        .eudamed-panel__busy-icon { animation: eudamed-clock 1.8s ease-in-out infinite; }
        @keyframes eudamed-clock {
          50% { transform: rotate(18deg); opacity: 0.65; }
        }
        @media (prefers-reduced-motion: reduce) {
          .eudamed-panel__busy-icon { animation: none; }
          .eudamed-panel__row,
          .eudamed-panel__secondary-action,
          .eudamed-panel__package-action,
          .eudamed-panel__download { transition: none; }
        }
      `}</style>
    </section>
  );
}

function RecordRow({ item, labels, downloadBusyId, hideGeneratedDocumentDownloads = false }) {
  const presentation = STATUS_PRESENTATION[item.status] || STATUS_PRESENTATION.info;
  const { Icon } = presentation;
  const busy = downloadBusyId === item.id;

  return (
    <li className="eudamed-panel__row" data-testid={`eudamed-item-${item.id}`}>
      <div className="eudamed-panel__row-copy">
        <div className="eudamed-panel__row-title">{item.title}</div>
        {item.detail ? <p className="eudamed-panel__row-detail">{item.detail}</p> : null}
      </div>
      <div className="eudamed-panel__row-actions">
        <span className={`eudamed-panel__status eudamed-panel__status--${presentation.tone}`}>
          <Icon size={13} weight="bold" aria-hidden="true" />
          {item.statusLabel}
        </span>
        {item.onAction ? (
          <button
            type="button"
            onClick={item.onAction}
            className="eudamed-panel__download"
            data-testid={`eudamed-item-action-${item.id}`}
          >
            <ArrowRight size={13} weight="bold" aria-hidden="true" />
            {item.actionLabel}
          </button>
        ) : null}
        {item.onDownload && !(hideGeneratedDocumentDownloads && ["document", "gspr"].includes(item.type)) ? (
          <button
            type="button"
            onClick={item.onDownload}
            disabled={busy}
            className="eudamed-panel__download"
            data-testid={`eudamed-item-download-${item.id}`}
            aria-label={item.downloadLabel}
          >
            {busy ? <Clock size={13} weight="bold" aria-hidden="true" /> : <ArrowDown size={13} weight="bold" aria-hidden="true" />}
            {busy ? labels.downloading : item.downloadLabel}
          </button>
        ) : null}
      </div>
    </li>
  );
}
